import { BoardGraph } from './graph';
import { isTilePassthrough } from './tiles';
import type { PathResult } from './types';

/**
 * Find the shortest path between two nodes using BFS.
 *
 * Movement cost rules:
 * - Walking/jumping/teleporting TO a non-pathing node costs 1 step.
 * - Walking/jumping/teleporting TO a pathing node costs 0 steps (passthrough).
 *   Spec §3: "Pathing spaces (invisible, not landable, no step consumed)"
 *
 * The returned path includes all nodes traversed (including pathing nodes),
 * but `distance` only counts non-pathing nodes (excluding the start node).
 *
 * @param graph - The board graph to search.
 * @param startId - Starting node ID.
 * @param endId - Target node ID.
 * @returns PathResult with path and distance. If no path exists, returns { path: [], distance: -1 }.
 */
export function findPath(graph: BoardGraph, startId: string, endId: string): PathResult {
  // Validate that both nodes exist
  if (!graph.getNode(startId) || !graph.getNode(endId)) {
    return { path: [], distance: -1 };
  }

  // Same-node shortcut
  if (startId === endId) {
    return { path: [startId], distance: 0 };
  }

  // BFS: queue entries hold the accumulated path to reach nodeId
  const queue: Array<{ nodeId: string; path: string[] }> = [{ nodeId: startId, path: [startId] }];
  const visited = new Set<string>([startId]);

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const { node: neighbor } of graph.getNeighbors(current.nodeId)) {
      if (visited.has(neighbor.id)) continue;
      visited.add(neighbor.id);

      const newPath = [...current.path, neighbor.id];

      if (neighbor.id === endId) {
        // Compute distance: count non-pathing nodes in path, excluding start node
        const distance = newPath
          .slice(1) // exclude start
          .filter((id) => {
            const n = graph.getNode(id);
            return n !== undefined && !isTilePassthrough(n.type);
          }).length;
        return { path: newPath, distance };
      }

      queue.push({ nodeId: neighbor.id, path: newPath });
    }
  }

  // No path found
  return { path: [], distance: -1 };
}

/**
 * Find all nodes reachable within N movement steps from a starting node.
 * Used by the movement executor (Plan 3a) to determine where a player can land after a dice roll.
 *
 * Movement cost rules same as findPath.
 * A node is "reachable" if the player would land on it (landable tiles only)
 * after exactly N steps. Pathing nodes do not consume steps but ARE traversed.
 *
 * @param graph - The board graph.
 * @param startId - Starting node ID.
 * @param steps - Number of movement steps (dice roll result).
 * @returns Array of node IDs where the player could land after exactly `steps` steps.
 *          Includes fork-reachable nodes (if there is a fork, all branches are explored).
 */
export function findReachable(graph: BoardGraph, startId: string, steps: number): string[] {
  if (steps < 0) return [];

  const startNode = graph.getNode(startId);
  if (!startNode) return [];

  if (steps === 0) {
    // Player stays: only collect if startId is landable (not a pathing node)
    return isTilePassthrough(startNode.type) ? [] : [startId];
  }

  // BFS with remaining-steps tracking.
  // Queue entries: { nodeId, remainingSteps }
  // We DO allow revisiting the same node with the same remainingSteps to handle cycles,
  // but we track (nodeId, remainingSteps) pairs to avoid infinite loops.
  const queue: Array<{ nodeId: string; remainingSteps: number }> = [
    { nodeId: startId, remainingSteps: steps },
  ];
  const visitedWithSteps = new Set<string>([`${startId}:${steps}`]);
  const reachable = new Set<string>();

  while (queue.length > 0) {
    const current = queue.shift()!;

    for (const { node: neighbor } of graph.getNeighbors(current.nodeId)) {
      // Cost of moving TO neighbor: 0 if passthrough (pathing), 1 otherwise
      const cost = isTilePassthrough(neighbor.type) ? 0 : 1;
      const newRemaining = current.remainingSteps - cost;

      if (newRemaining < 0) continue;

      const stateKey = `${neighbor.id}:${newRemaining}`;
      if (visitedWithSteps.has(stateKey)) continue;
      visitedWithSteps.add(stateKey);

      if (newRemaining === 0) {
        // Player used up all steps — only collect if the tile is landable
        if (!isTilePassthrough(neighbor.type)) {
          reachable.add(neighbor.id);
        }
        // Even if it's a pathing node at 0 remaining steps, we don't collect it —
        // pathing nodes are not landable. We do NOT continue from it (no more steps).
      } else {
        // Still have steps remaining — keep exploring
        queue.push({ nodeId: neighbor.id, remainingSteps: newRemaining });
      }
    }
  }

  return Array.from(reachable);
}
